import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from './AppIcon';

const MilestonePopup = ({ milestone, isOpen, onClose }) => {
  const [animationPhase, setAnimationPhase] = useState('enter');

  useEffect(() => {
    if (isOpen) {
      setAnimationPhase('enter');
      
      // Trigger celebration animation
      const timer1 = setTimeout(() => {
        setAnimationPhase('celebrate');
      }, 100);

      // Auto close after 5 seconds
      const timer2 = setTimeout(() => {
        handleClose();
      }, 5000);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [isOpen]);

  const handleClose = () => {
    setAnimationPhase('exit');
    setTimeout(() => {
      onClose?.();
    }, 300);
  };

  if (!isOpen || !milestone) return null;

  // Metric-specific colors
  const metricColors = {
    steps: { bg: 'from-primary/20 via-card to-primary/10', icon: 'text-primary', particle: 'bg-primary' },
    calories: { bg: 'from-warning/20 via-card to-warning/10', icon: 'text-warning', particle: 'bg-warning' },
    sleep: { bg: 'from-secondary/20 via-card to-secondary/10', icon: 'text-secondary', particle: 'bg-secondary' },
    water: { bg: 'from-accent/20 via-card to-accent/10', icon: 'text-accent', particle: 'bg-accent' }
  };

  const metricIcons = {
    steps: 'Footprints',
    calories: 'Flame',
    sleep: 'Moon',
    water: 'Droplets'
  };

  const colors = metricColors?.[milestone?.metric] || metricColors?.steps;
  const iconName = metricIcons?.[milestone?.metric] || 'Award';

  const modalContent = (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none">
      <AnimatePresence>
        {animationPhase !== 'exit' && (
          <motion.div
            initial={{ scale: 0.5, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: -20 }}
            transition={{ 
              type: 'spring', 
              damping: 20, 
              stiffness: 300,
              opacity: { duration: 0.3 }
            }}
            className="pointer-events-auto"
          >
            <div 
              className={`relative w-full max-w-sm bg-gradient-to-br ${colors?.bg} rounded-2xl shadow-2xl overflow-hidden cursor-pointer`}
              onClick={handleClose}
            >
              {/* Confetti Particles */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {[...Array(15)]?.map((_, i) => (
                  <motion.div
                    key={i}
                    className={`absolute w-2 h-2 ${colors?.particle} rounded-full`}
                    initial={{
                      x: '50%',
                      y: '50%',
                      scale: 0,
                      opacity: 0
                    }}
                    animate={{
                      x: `${50 + (Math.random() - 0.5) * 200}%`,
                      y: `${50 + (Math.random() - 0.5) * 200}%`,
                      scale: [0, 1, 0.8, 0],
                      opacity: [0, 1, 0.8, 0],
                      rotate: Math.random() * 360
                    }}
                    transition={{
                      duration: 2 + Math.random(),
                      delay: Math.random() * 0.3,
                      ease: 'easeOut'
                    }}
                  />
                ))}
              </div>

              {/* Content */}
              <div className="relative p-6 text-center space-y-4">
                {/* Milestone Icon with Pulsing Effect */}
                <motion.div 
                  className="relative mx-auto w-24 h-24"
                  animate={{
                    scale: animationPhase === 'celebrate' ? [1, 1.1, 1] : 1
                  }}
                  transition={{
                    duration: 0.6,
                    repeat: animationPhase === 'celebrate' ? Infinity : 0,
                    repeatDelay: 0.5
                  }}
                >
                  {/* Pulsing Background Circles */}
                  <motion.div 
                    className={`absolute inset-0 ${colors?.particle} opacity-20 rounded-full`}
                    animate={{
                      scale: [1, 1.3, 1],
                      opacity: [0.2, 0, 0.2]
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: 'easeInOut'
                    }}
                  />
                  <motion.div 
                    className={`absolute inset-0 ${colors?.particle} opacity-30 rounded-full`}
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.3, 0, 0.3]
                    }}
                    transition={{
                      duration: 1.2,
                      repeat: Infinity,
                      ease: 'easeInOut'
                    }}
                  />
                  
                  {/* Icon Circle */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${colors?.bg} rounded-full flex items-center justify-center shadow-lg border-4 border-card`}>
                    <Icon 
                      name={iconName}
                      size={40} 
                      className={colors?.icon}
                    />
                  </div>
                </motion.div>

                {/* Emoji */}
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ 
                    type: 'spring',
                    delay: 0.2,
                    damping: 15,
                    stiffness: 200
                  }}
                  className="text-5xl"
                >
                  {milestone?.emoji}
                </motion.div>

                {/* Title */}
                <motion.h2 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-2xl font-bold text-foreground"
                >
                  {milestone?.title}
                </motion.h2>

                {/* Message */}
                <motion.p 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="text-base text-muted-foreground"
                >
                  {milestone?.message}
                </motion.p>

                {/* Value Display */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 }}
                  className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full ${colors?.particle} bg-opacity-10`}
                >
                  <Icon name="TrendingUp" size={20} className={colors?.icon} />
                  <span className={`text-xl font-bold ${colors?.icon}`}>
                    {milestone?.value?.toLocaleString()}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {milestone?.metric === 'steps' && 'steps'}
                    {milestone?.metric === 'calories' && 'kcal'}
                    {milestone?.metric === 'sleep' && 'hours'}
                    {milestone?.metric === 'water' && 'glasses'}
                  </span>
                </motion.div>

                {/* Tap to dismiss hint */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.6 }}
                  transition={{ delay: 1 }}
                  className="text-xs text-muted-foreground pt-2"
                >
                  Tap to dismiss
                </motion.p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default MilestonePopup;